/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Leonid Khramov, Tabitha Bleil
 */
import { connect } from "react-redux"
import React, { useState, useEffect } from "react"

import {
    Element,
    Label,
    Button,
    TitleSection,
    ContentSection,
    FooterSection,
    Input,
    Modal,
    Color,
} from "trux"
import * as links from "../../../util/links"
import * as stat from "../../../util/analytics"
import {
    validateName,
    validateRosterEmail,
    validateJerseyNumber,
} from "../../../util/validator"

import ContactsEdit from "../components/ContactsEdit"
import * as playerActs from "../actions/players"
import { webApi } from "../../../lib/WebApi.js"
import { getPlayerEmail } from "../getPlayerEmail"

import "./ShowPlayer.css"

const EditPlayerComponent = (props) => {
    const [setup, setSetup] = useState(null)
    const [name, setName] = useState("")
    const [email, setEmail] = useState("")
    const [jerseyNumber, setJerseyNumber] = useState("")
    const showJerseyNumber = props.showJerseyNumber
    const [contacts, setContacts] = useState([])
    const [contactsToRemove, setContactsToRemove] = useState([])
    const [errors, setErrors] = useState({
        name: null,
        email: null,
        jersey: null,
        contactsEmail: [],
        ready: true,
    })
    const [userError, setUserError] = useState(false)

    useEffect(() => {
        setSetup(false)
        stat.statPageView()
    }, [])

    useEffect(() => {
        if (setup === false && props.loaded === true) {
            if (props.player.contacts != null) {
                var propsContacts = props.player.contacts
                if (propsContacts.length === 0) {
                    propsContacts.push({
                        _editable: true,
                        raw_value: "",
                        first_name: "",
                        last_name: "",
                        email: "",
                        relation_type: "child",
                    })
                }
                setSetup(true)
                setName(
                    `${props.player.user.first_name} ${props.player.user.last_name}`
                )
                setEmail(getPlayerEmail(props.player))
                setJerseyNumber(props.player.jersey_number)
                setContacts(propsContacts)
            }
        }
    }, [props.player.contacts, setup])

    useEffect(() => {
        if (
            props.editPlayerMatchingUser &&
            props.editPlayerError === "email_existing_user"
        ) {
            setUserError(true)
        }
    }, [props.editPlayerError])

    const onEmailChanged = (val) => {
        setEmail(val)
        if (!validateRosterEmail(val)) {
            setErrors({ ...errors, email: false })
        }
        if (userError) {
            setUserError(false)
            props.resetEditPlayer()
        }
    }

    const onJerseyNumberChanged = (val) => {
        setJerseyNumber(val)
        if (!validateJerseyNumber(val)) {
            setErrors({ ...errors, jersey: false })
        }
    }

    const onSubmit = (e) => {
        e.preventDefault()
        var names = name.split(" ")

        var first = ""
        var last = ""
        for (var i in names) {
            if (i < 1) {
                first += " " + names[i]
            } else {
                last += " " + names[i]
            }
        }
        first = first.trim()
        last = last.trim()

        var attr = {
            first_name: first,
            last_name: last,
            sport_type: props.team.sport_type,
            jersey_number: jerseyNumber,
        }
        // add email if its not masked
        let cleanEmailStr = email.trim()
        // must check orig email this way in case where user editing cannot see unmasked email
        let origEmailCheck = true
        if (props.player.email) {
            origEmailCheck = props.player.email !== cleanEmailStr
        }
        if (cleanEmailStr && !/\*+/gi.test(email) && origEmailCheck) {
            attr["email"] = email
        }

        var addRelations = []
        for (i in contacts) {
            var rel = contacts[i]
            //we add new relations this name and email filled
            if (
                rel._editable === true &&
                rel.first_name.length > 0 &&
                rel.email.length > 0
            ) {
                var nameParts = rel.first_name.split(" ")
                first = ""
                last = ""
                for (i in nameParts) {
                    if (i < 1) {
                        first += " " + nameParts[i]
                    } else {
                        last += " " + nameParts[i]
                    }
                }
                var relAttr = Object.assign({}, rel)
                relAttr.first_name = first.trim()
                relAttr.last_name = last.trim()

                addRelations.push(relAttr)
            }
        }

        var playerId = props.player.team_player_id
        var userId = props.player.user_id
        let removeRelations = contactsToRemove

        if (validate()) {
            props.updatePlayer(
                playerId,
                userId,
                attr,
                addRelations,
                removeRelations,
                (success, err) => {
                    if (success) {
                        close()
                        // simple hack to refresh rosters
                        if (props.match.params.roster_type) {
                            setTimeout(() => {
                                window.location.reload(true)
                            }, 200)
                        }
                    } else if (err !== "email_existing_user") {
                        webApi.postSlack(
                            `Roster Add Player Error: ${err} ${props.user.email} ${window.location.href} name:${name} email:${email}`,
                            null,
                            null,
                            "CJDN93L7J"
                        )
                        alert(
                            "Something went wrong. Trace support has been alerted. We will resolve the issue and get back to you asap. You can try a different email address or come back later."
                        )
                    }
                }
            )
        }
    }

    const getEmailDuplicates = () => {
        var emails = contacts.map((c) => {
            if (c._editable) {
                return c.email
            }
            return null
        })
        if (email !== props.player.user.masked_email && email.length > 0) {
            emails.push(email)
        }
        var dupes = []
        for (var a in emails) {
            for (var b in emails) {
                if (emails[a] === emails[b] && a !== b) {
                    dupes.push(emails[a])
                }
            }
        }
        return dupes
    }

    const validate = () => {
        var nameError = null
        var emailError = null
        var jerseyError = null
        var ready = true

        nameError = validateName(name)
        emailError = validateRosterEmail(email)
        jerseyError = validateJerseyNumber(jerseyNumber)

        if (nameError || emailError || jerseyError) {
            ready = false
        }

        var dupes = getEmailDuplicates()

        var stateContacts = contacts
        for (var contact of stateContacts) {
            contact.duplicate_email = false

            if (contact.email.length > 0) {
                contact.invalid_email = validateRosterEmail(contact.email)
                if (dupes.includes(contact.email)) {
                    contact.duplicate_email = true
                }
            }
            if (contact.first_name.length > 0) {
                contact.invalid_name = validateName(contact.first_name)
            }

            if (
                contact.invalid_name ||
                contact.invalid_email ||
                contact.duplicate_email
            ) {
                ready = false
            }
        }

        setContacts(stateContacts)
        setErrors({
            name: nameError,
            email: emailError,
            jersey: jerseyError,
            ready: ready,
        })

        return ready
    }

    const cancel = (e) => {
        close(e)
    }

    const close = (e) => {
        if (e) {
            e.preventDefault()
        }

        props.resetEditPlayer()

        if (props.match.params.roster_type) {
            // Roster App
            props.history.replace(
                `/${props.teamId}/rosters/${props.rosterType}/${props.rosterId}`
            )
        } else {
            // Video App
            var url = links.gamePlayerDetailsUrl(
                props.match.params.team_name,
                props.match.params.playlist_id,
                props.match.params.game_id,
                props.match.params.event_number,
                props.match.params.trace_number
            )
            props.history.replace(url)
        }
    }

    const onContactNameChanged = (index, val, first, last) => {
        var stateContacts = [...contacts]
        stateContacts[index].raw_value = val
        stateContacts[index].first_name = val
        stateContacts[index].last_name = last
        if (!validateName(val)) {
            stateContacts[index].invalid_name = false
        }
        setContacts(stateContacts)
    }

    const onContactEmailChanged = (index, email) => {
        var stateContacts = [...contacts]
        stateContacts[index].email = email
        if (!validateRosterEmail(email)) {
            stateContacts[index].invalid_email = false
        }
        setContacts(stateContacts)
    }

    const onContactAdd = () => {
        var stateContacts = contacts
        stateContacts.push({
            _editable: true,
            raw_value: "",
            first_name: "",
            last_name: "",
            email: "",
            relation_type: "child",
        })
        setContacts(stateContacts)
        validate()
    }

    const onContactRemove = (i) => {
        var stateContacts = contacts
        var removed = stateContacts.splice(i, 1)
        var toRemove = contactsToRemove
        if (removed.length > 0) {
            if (removed[0].user_relationship_id) {
                toRemove.push(removed[0])
            }
            setContacts(stateContacts)
            setContactsToRemove(toRemove)
        }
        validate()
    }

    const noAddingNew = (e) => {
        if (e) {
            e.preventDefault()
        }
        setEmail(getPlayerEmail(props.player))
        setUserError(false)
        props.resetEditPlayer()
    }

    const handleDuplicateContact = (index, use) => {
        var stateContacts = [...contacts]
        var contact = stateContacts[index]
        if (use) {
            for (var i in props.editPlayerMatchingRelations) {
                var rel = props.editPlayerMatchingRelations[i]
                if (rel.email === contact.email) {
                    contact.raw_value = rel.first_name + " " + rel.last_name
                    contact.first_name = rel.first_name + " " + rel.last_name
                    contact.last_name = ""
                    var matching = props.editPlayerMatchingRelations.filter(
                        (r) => {
                            return r.email !== contact.email
                        }
                    )
                    props.updateEditPlayer(
                        props.editPlayerError,
                        props.editPlayerMatchingUser,
                        matching
                    )
                    break
                }
            }
            setContacts(stateContacts)
        } else {
            for (i in props.editPlayerMatchingRelations) {
                rel = props.editPlayerMatchingRelations[i]
                if (rel.email === contact.email) {
                    matching = props.editPlayerMatchingRelations.filter((r) => {
                        return r.email !== contact.email
                    })
                    props.updateEditPlayer(
                        props.addPlayerError,
                        props.addPlayerMatchingUser,
                        matching
                    )
                    contact.email = ""
                    break
                }
            }
            setContacts(stateContacts)
        }
    }

    return (
        <Modal
            onRequestClose={cancel}
            onShadeClick={close}
            open={props.isLoggedIn === true}
            className="player-modal edit-player"
        >
            <TitleSection onCloseClick={close}>
                <Label
                    center
                    type={Label.Type.Heading4}
                    content="Edit Player"
                />
            </TitleSection>
            <ContentSection>
                <PlayerHeader
                    setup={setup}
                    playerTraceNumber={props.player.default_trace_number}
                />
                <Input
                    id="edit-player-name"
                    type={Input.Type.Text}
                    value={name}
                    disabled
                />
                <Input
                    name="email"
                    label="Email"
                    type={Input.Type.Text}
                    value={email}
                    onChange={onEmailChanged}
                    onBlur={validate}
                    error={errors.email}
                />
                {userError && (
                    <Label
                        className="duplicate-error"
                        color={Color.Error}
                        type={Label.Type.SmallBody}
                    >
                        Email already used by another player, it must be unique.
                        &nbsp; &nbsp;
                        <Element
                            tag="a"
                            id="duplicate-error-ok"
                            href="#no"
                            onClick={noAddingNew}
                        >
                            OK
                        </Element>
                    </Label>
                )}

                {showJerseyNumber && (
                    <Input
                        name="jersey-number"
                        label="Jersey Number"
                        type={Input.Type.Number}
                        value={jerseyNumber}
                        onChange={onJerseyNumberChanged}
                        onBlur={validate}
                        error={errors.jersey}
                        width={130}
                    />
                )}
                <ContactsEdit
                    contacts={contacts}
                    onNameChanged={onContactNameChanged}
                    onEmailChanged={onContactEmailChanged}
                    onAddContact={onContactAdd}
                    onRemoveContact={onContactRemove}
                    duplicateEmails={props.editPlayerMatchingRelations}
                    useDuplicateContact={handleDuplicateContact}
                    validate={validate}
                    validateContactNameChanged={validate}
                    validateContactEmailChanged={validate}
                />
                <Label
                    className="fine-print"
                    type={Label.Type.SmallBody}
                    center
                    marginLeft="17"
                    marginRight="17"
                >
                    By clicking &ldquo;Save&rdquo;, you agree to our{" "}
                    <Element
                        tag="a"
                        href="https://www.traceup.com/terms-of-service"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        Terms
                    </Element>{" "}
                    and that you have read our{" "}
                    <Element
                        tag="a"
                        href="https://www.traceup.com/privacy"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        Privacy Policy
                    </Element>
                </Label>
            </ContentSection>
            <FooterSection padding="4">
                <Button
                    trkAction="Cancel Pressed"
                    width="100%"
                    id="button-cancel"
                    className="button-cancel"
                    content={<Label>Cancel</Label>}
                    onClick={cancel}
                />
                <Button
                    trkAction="Save Pressed"
                    width="100%"
                    className="button-save"
                    primary
                    content={<Label>Save</Label>}
                    marginLeft="2"
                    disabled={!errors.ready}
                    onClick={onSubmit}
                />
            </FooterSection>
        </Modal>
    )
}

const PlayerHeader = ({ setup, playerTraceNumber }) => {
    let emptyClass = "empty"
    let value = "?"
    let num = parseInt(playerTraceNumber)
    if (num && num !== -1) {
        value = num
        emptyClass = ""
    }

    if (setup) {
        return (
            <Element className="heading-text" marginBottom={5}>
                <Label type={Label.Type.Heading4} className="title">
                    Player
                </Label>
                <Element className={`number-shield ${emptyClass}`}>
                    <Element className={`number ${emptyClass}`}>
                        {value}
                    </Element>
                </Element>
            </Element>
        )
    } else {
        return (
            <Element className="heading-text">
                <Label type={Label.Type.Heading4} className="title">
                    Loading ...
                </Label>
            </Element>
        )
    }
}

const mapStateToProps = (state, props) => {
    // NOTE: tracer number stuff will be removed, no more updates to it

    let loaded = state.team.complete && state.roster.complete
    let teamId = ""
    let rosterType = "games"
    let rosterId = new Date().getFullYear()
    let team = null
    let playerId = 0
    let player = {
        default: true,
        default_trace_number: -1,
        user: { first_name: "", last_name: "", email: "" },
    }
    let allPlayers = []
    let isLoggedIn = false
    let showJerseyNumber = true
    let rev = new Date().getTime()
    let editPlayerInProgress = false
    let editPlayerError = null
    let editPlayerMatchingUser = null
    let editPlayerMatchingRelations = []

    // Team Id
    if (props.match.params.team) {
        teamId = props.match.params.team
    } else {
        teamId = props.match.params.team_name
    }

    // Roster Type
    if (props.match.params.roster_type) {
        rosterType = props.match.params.roster_type
    }

    // Roster Id
    if (rosterType !== "season") {
        if (props.match.params.roster_id) {
            rosterId = parseInt(props.match.params.roster_id, 10)
        } else {
            rosterId = parseInt(props.match.params.game_id, 10)
        }
    }

    // team
    team = state.team.value

    // player id
    if (props.match.params.player_id) {
        playerId = parseInt(props.match.params.player_id, 10)
    } else {
        playerId = parseInt(props.match.params.trace_number, 10)
    }

    // jersey numbers are enabled for all teams, at least for now
    // if (state.acl.value.show_jersey_numbers != null) {
    //     showJerseyNumber = state.acl.value.show_jersey_numbers
    // }

    // all players
    if (state.roster.value) {
        allPlayers = state.roster.value
    }

    // Player
    if (state.roster.value) {
        for (var i in state.roster.value) {
            if (state.roster.value[i].team_player_id === playerId) {
                player = state.roster.value[i]
                player.contacts = state.relations.value[player.user_id]
                break
            }
        }
        if (player.default === true) {
            for (i in state.roster.value) {
                if (
                    parseInt(state.roster.value[i].default_trace_number, 10) ===
                    playerId
                ) {
                    player = state.roster.value[i]
                    playerId = player.team_player_id
                    player.contacts = state.relations.value[player.user_id]
                    break
                }
            }
        }
    }

    // logged in
    isLoggedIn = state.isTeamLoggedIn

    editPlayerInProgress = state.misc.value.editPlayerInProgress
    editPlayerError = state.misc.value.editPlayerError
    editPlayerMatchingUser = state.misc.value.editPlayerMatchingUser
    editPlayerMatchingRelations = state.misc.value.editPlayerMatchingRelations

    return {
        loaded,
        teamId,
        rosterType,
        rosterId,
        team,
        playerId,
        player,
        allPlayers,
        isLoggedIn,
        showJerseyNumber,
        rev,
        editPlayerInProgress,
        editPlayerError,
        editPlayerMatchingUser,
        editPlayerMatchingRelations,
        user: state.user.value,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        updatePlayer: (
            playerId,
            userId,
            attr,
            addRelations,
            removeRelations,
            cb
        ) => {
            dispatch(
                playerActs.updatePlayer(
                    playerId,
                    userId,
                    attr,
                    addRelations,
                    removeRelations,
                    null,
                    cb
                )
            )
        },
        resetEditPlayer: () => {
            dispatch({
                type: "COMMON_SET",
                key: "misc",
                value: {
                    value: {
                        editPlayerInProgress: false,
                        editPlayerError: null,
                        editPlayerMatchingUser: null,
                        editPlayerMatchingRelations: null,
                    },
                },
            })
        },
        updateEditPlayer: (err, user, rels) => {
            dispatch({
                type: "COMMON_SET",
                key: "misc",
                value: {
                    value: {
                        editPlayerInProgress: false,
                        editPlayerError: err,
                        editPlayerMatchingUser: user,
                        editPlayerMatchingRelations: rels,
                    },
                },
            })
        },
    }
}

const EditPlayer = connect(
    mapStateToProps,
    mapDispatchToProps
)(EditPlayerComponent)

export default EditPlayer
